// Subtle notification sound via Web Audio API — no audio file needed.
// One soft 2-tone chime (E5 → A5, 180ms total). Sandbox-safe across iOS Safari,
// Android Chrome, and desktop browsers. Mobile-friendly autoplay handling:
//  - AudioContext is created lazily on first user gesture (handled by the
//    document-level click listener installed in armAudio()).
//  - If the context is still 'suspended' when we attempt to play, we no-op.

const PREF_KEY = "vowos_notif_sound";
let ctx = null;
let armed = false;

const isOn = () => {
  try { return localStorage.getItem(PREF_KEY) !== "off"; } catch (e) { return true; }
};
export const isSoundOn = () => isOn();
export const setSoundOn = (on) => {
  try { localStorage.setItem(PREF_KEY, on ? "on" : "off"); } catch (e) { /* noop */ }
};
export const initSoundPreference = (defaultOn) => {
  try {
    if (localStorage.getItem(PREF_KEY) === null) {
      localStorage.setItem(PREF_KEY, defaultOn ? "on" : "off");
    }
  } catch (e) { /* noop */ }
};

const getCtx = () => {
  if (ctx) return ctx;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  try { ctx = new AC(); } catch (e) { return null; }
  return ctx;
};

// Arm the audio context on the FIRST user gesture anywhere on the page.
// iOS Safari requires this — playback inside a setInterval poll without a
// prior user gesture is silently blocked.
export const armAudio = () => {
  if (armed) return;
  armed = true;
  const handler = () => {
    const c = getCtx();
    if (c && c.state === "suspended") c.resume().catch(() => {});
    document.removeEventListener("click", handler, true);
    document.removeEventListener("touchstart", handler, true);
    document.removeEventListener("keydown", handler, true);
  };
  document.addEventListener("click", handler, true);
  document.addEventListener("touchstart", handler, true);
  document.addEventListener("keydown", handler, true);
};

export const playNotificationChime = () => {
  if (!isOn()) return;
  const c = getCtx();
  if (!c || c.state !== "running") return;
  const now = c.currentTime;
  const master = c.createGain();
  master.gain.value = 0.0;
  master.connect(c.destination);

  // Tone 1: E5 (659.25 Hz) — softer attack
  const o1 = c.createOscillator();
  o1.type = "sine";
  o1.frequency.value = 659.25;
  const g1 = c.createGain();
  g1.gain.setValueAtTime(0.0001, now);
  g1.gain.exponentialRampToValueAtTime(0.42, now + 0.015);
  g1.gain.exponentialRampToValueAtTime(0.0001, now + 0.14);
  o1.connect(g1).connect(master);
  o1.start(now);
  o1.stop(now + 0.15);

  // Tone 2: A5 (880 Hz) — light follow-up
  const o2 = c.createOscillator();
  o2.type = "sine";
  o2.frequency.value = 880;
  const g2 = c.createGain();
  g2.gain.setValueAtTime(0.0001, now + 0.09);
  g2.gain.exponentialRampToValueAtTime(0.36, now + 0.105);
  g2.gain.exponentialRampToValueAtTime(0.0001, now + 0.25);
  o2.connect(g2).connect(master);
  o2.start(now + 0.09);
  o2.stop(now + 0.26);

  master.gain.setValueAtTime(0.0001, now);
  master.gain.exponentialRampToValueAtTime(1.0, now + 0.01);
  master.gain.exponentialRampToValueAtTime(0.0001, now + 0.28);
};
